import React, { useState } from "react";
import { Link } from "react-router-dom";
import { auth } from "../firebase";
import { sendPasswordResetEmail } from "firebase/auth";

// Platform-wide password reset. Fund and Reader link here from their own login
// pages, so this is the one place the reset email is sent from.
const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError("");

      await sendResetEmail(email.trim());
      setSent(true);
    } catch (err) {
      // Never reveal whether an account exists for this address.
      if (err?.code === "auth/user-not-found") {
        setSent(true);
      } else {
        console.error("Password reset failed:", err);
        setError(getFriendlyResetError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <p style={eyebrowStyle}>Antarctic Press Portal</p>

        <h1 style={titleStyle}>Reset your password</h1>

        {sent ? (
          <>
            <div style={successStyle}>
              If an account exists for <strong>{email.trim()}</strong>, we've
              sent it an email with a link to choose a new password.
            </div>
            <p style={subtitleStyle}>
              The link expires after one hour. If you don't see the email in a
              few minutes, check your spam or junk folder.
            </p>
            <button
              type="button"
              style={secondaryButtonStyle}
              onClick={() => setSent(false)}
            >
              Send it again
            </button>
          </>
        ) : (
          <>
            <p style={subtitleStyle}>
              Enter the email address you use to log in and we'll send you a
              link to choose a new password.
            </p>

            {error ? <div style={errorStyle}>{error}</div> : null}

            <form onSubmit={handleSubmit} style={formStyle}>
              <label style={labelStyle}>
                Email
                <input
                  type="email"
                  value={email}
                  autoComplete="email"
                  onChange={(e) => setEmail(e.target.value)}
                  style={inputStyle}
                  disabled={loading}
                  required
                />
              </label>

              <button type="submit" style={primaryButtonStyle} disabled={loading}>
                {loading ? "Sending..." : "Send reset link"}
              </button>
            </form>
          </>
        )}

        <p style={footerTextStyle}>
          Remembered it?{" "}
          <Link to="/portal/login" style={linkStyle}>
            Back to log in
          </Link>
        </p>

        <p style={noteStyle}>
          Signed up with Google? You don't have a separate password. Use
          "Continue with Google" on the log in page instead.
        </p>
      </div>
    </div>
  );
};

// The continue URL returns the user to this site's login page after they set a
// new password. Firebase rejects it if the current host is missing from Auth's
// authorized domains; fall back to Firebase's default page rather than fail.
async function sendResetEmail(email) {
  const actionCodeSettings = {
    url: `${window.location.origin}/portal/login`,
  };

  try {
    await sendPasswordResetEmail(auth, email, actionCodeSettings);
  } catch (err) {
    if (err?.code === "auth/unauthorized-continue-uri") {
      await sendPasswordResetEmail(auth, email);
      return;
    }
    throw err;
  }
}

function getFriendlyResetError(err) {
  switch (err?.code) {
    case "auth/invalid-email":
    case "auth/missing-email":
      return "Please enter a valid email address.";
    case "auth/too-many-requests":
      return "Too many attempts. Please wait a few minutes and try again.";
    case "auth/network-request-failed":
      return "We couldn't reach the server. Check your connection and try again.";
    default:
      return "Something went wrong while sending the reset email.";
  }
}

const pageStyle = {
  minHeight: "calc(100vh - 80px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "3rem 1rem",
  background: "#f3f6fa",
};

const cardStyle = {
  width: "100%",
  maxWidth: "460px",
  background: "#fff",
  border: "1px solid #d7e2ef",
  borderTop: "5px solid #114c96",
  borderRadius: "14px",
  padding: "2rem",
  boxShadow: "0 10px 28px rgba(0,0,0,0.08)",
};

const eyebrowStyle = {
  margin: "0 0 0.5rem 0",
  color: "#114c96",
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  fontSize: "0.78rem",
  fontWeight: 800,
};

const titleStyle = {
  margin: "0 0 0.75rem 0",
  color: "#111827",
  fontSize: "2rem",
  fontWeight: 900,
};

const subtitleStyle = {
  margin: "0 0 1.5rem 0",
  color: "#4b5563",
  lineHeight: 1.6,
};

const formStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
};

const labelStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "0.4rem",
  color: "#111827",
  fontWeight: 700,
};

const inputStyle = {
  padding: "0.8rem 0.9rem",
  border: "1px solid #cbd5e1",
  borderRadius: "8px",
  fontSize: "1rem",
};

const primaryButtonStyle = {
  marginTop: "0.5rem",
  background: "#114c96",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  padding: "0.85rem 1rem",
  fontWeight: 800,
  cursor: "pointer",
};

const secondaryButtonStyle = {
  width: "100%",
  background: "#fff",
  color: "#111827",
  border: "1px solid #cbd5e1",
  borderRadius: "8px",
  padding: "0.85rem 1rem",
  fontWeight: 800,
  cursor: "pointer",
};

const errorStyle = {
  background: "#fff1f2",
  color: "#be123c",
  border: "1px solid #fecdd3",
  borderRadius: "8px",
  padding: "0.85rem",
  marginBottom: "1rem",
  fontWeight: 700,
};

const successStyle = {
  background: "#f0fdf4",
  color: "#166534",
  border: "1px solid #bbf7d0",
  borderRadius: "8px",
  padding: "0.85rem",
  marginBottom: "1rem",
  fontWeight: 600,
  lineHeight: 1.5,
};

const footerTextStyle = {
  margin: "1.5rem 0 0 0",
  color: "#4b5563",
  textAlign: "center",
};

const noteStyle = {
  margin: "1rem 0 0 0",
  color: "#64748b",
  fontSize: "0.85rem",
  textAlign: "center",
  lineHeight: 1.5,
};

const linkStyle = {
  color: "#114c96",
  fontWeight: 800,
};

export default ForgotPasswordPage;
